import french from './translations/fr/content.json';
import { getLanguage } from './TextManager';

// English text is the lookup key; identifiers and rich-text tokens stay unchanged.
export function translate(text, language = getLanguage()) {
  return language === 'fr' && typeof text === 'string' ? (french[text] ?? text) : text;
}

// The first part identifies the status; everything after the first slash is display text.
export function parseKeyword(token) {
  const separator = token.indexOf('/');
  return separator < 0
    ? { keyword: token, alias: null }
    : { keyword: token.slice(0, separator), alias: token.slice(separator + 1) || null };
}

export function keywordText(token, language = getLanguage(), defaultLabel) {
  const { keyword, alias } = parseKeyword(token);
  return alias === null ? (defaultLabel ?? translate(keyword, language)) : translate(alias, language);
}

export function plainText(text, language = getLanguage()) {
  return typeof text === 'string'
    ? translate(text, language).replace(/\[([^\]]+)\]/g, (_, label) => keywordText(label, language))
    : text;
}

const fields = new Set(['name', 'path', 'effect', 'description', 'text', 'speaker', 'quote', 'dialog', 'tooltip', 'title', 'requirement', 'reward', 'passive']);
const logic = new Set(['blueprint', 'modifiers', 'check', 'pickup', 'bonus']);

export function localizeContent(value, language = getLanguage(), dialogue = false) {
  if (typeof value === 'string') return dialogue ? translate(value, language) : value;
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(entry => localizeContent(entry, language, dialogue));
  const result = {};
  for (const [key, entry] of Object.entries(value)) {
    if (key === 'translations' || logic.has(key)) result[key] = entry;
    else if (typeof entry === 'string' && fields.has(key)) {
      result[key] = translate(entry, language);
    } else {
      result[key] = localizeContent(entry, language, dialogue || key === 'dialogs');
      // Passive names are lookup keys used by combat rules, not display labels.
      if (key === 'passive' && entry && typeof entry === 'object' && entry.name) result[key].name = entry.name;
    }
  }
  if (value.name) result.originalName = value.originalName || value.name;
  if (value.translations) {
    const english = value.translations.en || {};
    const translated = language === 'fr'
      ? Object.fromEntries(Object.entries(english).map(([key, text]) => [key, translate(text, language)]))
      : {};
    result.translations = {
      ...value.translations,
      [language]: { ...english, ...translated, ...value.translations[language] }
    };
  }
  // Upgrade effects use language-code properties in the existing library.
  if (language === 'fr' && typeof value.en === 'string') result.fr = translate(value.en, language);
  return result;
}
